import { array, either, option, record } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { BrandAffinityLevel } from 'modules/domains/brandAffinity';
import { DecomposableRecord, NodeTrees } from 'ui/treeView';
import { PHONE_NUMBER, USER_ID, EMAIL, INTERNAL_USER_ID } from 'utils/fieldConstants';
import { emptyStringToUndefined, emptyStringOptionToUndefined, pluck, pluckOption } from 'utils/fp';
import { formatLocation, LocationProps } from 'utils/geoHelpers';
import { ensureEnum } from 'utils/tsHelpers';
import {
  Channel,
  Device,
  List,
  MessageType,
  SNS_PLATFORMS_TO_DEFAULT_MODEL_NAMES,
  SNS_PLATFORMS_TO_USER_CLIENT_TYPES,
  SnsPlatform,
  UserClientType,
} from '../domain';
import { useUserKeyParameter } from './useUserKeyParameter';
import { UserMetadataQueryResponse, useUserMetadataQuery } from './useUserMetadataQuery';

function getField<T>(fieldName: keyof UserMetadataQueryResponse) {
  return pluck<UserMetadataQueryResponse, T>(fieldName);
}

function getFieldValue<T>(fieldName: keyof UserMetadataQueryResponse) {
  return flow(getField<T>(fieldName), option.toUndefined);
}

const getDataFields = getField<NodeTrees>('dataFields');

function getDataField<T = string>(fieldName: keyof NodeTrees) {
  return flow(getDataFields, pluckOption<NodeTrees, T>(fieldName));
}

const getDataEmail = getDataField(EMAIL);

const getEmail = (data?: UserMetadataQueryResponse) =>
  pipe(
    data,
    getField<string>('email'),
    option.alt(() => getDataEmail(data)),
    emptyStringOptionToUndefined,
  );

const getPhoneNumber = flow(getDataField(PHONE_NUMBER), option.toUndefined);

const locationFields = ['city', 'region', 'country'];
const getLocation = (data?: UserMetadataQueryResponse) =>
  pipe(
    locationFields,
    array.map(getDataField), // apply fieldName to getDataField
    array.flap(data), // execute getDataField on data for each locationField
    array.map(option.getOrElse(() => '')), // ensure values resolve to a string
    array.mapWithIndex((index, value) => [locationFields[index], value] as [string, string]),
    record.fromEntries,
    (value) => value as LocationProps,
    formatLocation,
    emptyStringToUndefined,
  );

const DEFAULT_BRAND_AFFINITY = BrandAffinityLevel.UNSCORED;

const calculateBrandAffinity =
  (defaultValue = DEFAULT_BRAND_AFFINITY) =>
  (value?: string) =>
    ensureEnum(value, BrandAffinityLevel, defaultValue);

const getItblDs = (data?: UserMetadataQueryResponse): option.Option<DecomposableRecord> =>
  pipe(
    data,
    getDataField<DecomposableRecord>('itblDS'),
    either.fromOption(() => undefined),
    either.chain((itblDs: DecomposableRecord) =>
      typeof itblDs === 'object' && !Array.isArray(itblDs)
        ? either.right(itblDs)
        : either.left(undefined),
    ),
    option.fromEither,
  );

const getBrandAffinity = (data?: UserMetadataQueryResponse) =>
  pipe(
    data,
    getItblDs,
    pluckOption<DecomposableRecord, string>('brandAffinityLabel'),
    option.toUndefined,
    calculateBrandAffinity(),
  );

const getUserId = flow(getDataField(USER_ID), option.toUndefined);

const getItblUserId = flow(getDataField(INTERNAL_USER_ID), option.toUndefined);

const getChannels = getFieldValue<Channel[]>('channels');
const getMessageTypes = getFieldValue<MessageType[]>('messageTypes');
const getLists = getFieldValue<List[]>('userLists');

const getFirstDevice = flow(getField<Device[]>('devices'), option.map(array.head), option.compact);

const getClientNameFromPlatform = flow(
  getFirstDevice,
  option.map((device: Device) => device.platform),
  option.map((platform: SnsPlatform) => SNS_PLATFORMS_TO_DEFAULT_MODEL_NAMES[platform]),
);

const getClientModelName = flow(
  getFirstDevice,
  option.map((d) => d.model),
  option.map(option.fromNullable),
  option.compact,
);

const getClientName = (data?: UserMetadataQueryResponse) =>
  pipe(
    data,
    getClientModelName,
    option.alt(() => getClientNameFromPlatform(data)),
    emptyStringOptionToUndefined,
  );

const getClientType = flow(
  getFirstDevice,
  option.map((device: Device) => device.platform),
  option.map((platform: SnsPlatform) => SNS_PLATFORMS_TO_USER_CLIENT_TYPES[platform]),
  option.toUndefined,
);

const maybeGetDataFields = flow(getDataFields, option.toUndefined);

export type CalculatedProfileData = {
  isLoading: boolean;
  isError: boolean;
  emailAddress?: string;
  phoneNumber?: string;
  location?: string;
  clientType?: UserClientType;
  clientName?: string;
  internalUserId?: string;
  externalUserId?: string;
  brandAffinityLevel?: BrandAffinityLevel;
  allChannels?: Channel[];
  allMessageTypes?: MessageType[];
  allLists?: List[];
  dataFields?: NodeTrees;
};

export const useCalculateProfileData = (): CalculatedProfileData => {
  const userKey = useUserKeyParameter();
  const { isLoading, isError, data } = useUserMetadataQuery({
    userKey,
  });

  return {
    isLoading,
    isError,
    emailAddress: getEmail(data),
    phoneNumber: getPhoneNumber(data),
    location: getLocation(data),
    clientType: getClientType(data),
    clientName: getClientName(data),
    internalUserId: getItblUserId(data),
    externalUserId: getUserId(data),
    brandAffinityLevel: getBrandAffinity(data),
    allChannels: getChannels(data),
    allMessageTypes: getMessageTypes(data),
    allLists: getLists(data),
    dataFields: maybeGetDataFields(data),
  };
};
